import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './SuperAdminPage.css';

const SuperAdminPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [batchAction, setBatchAction] = useState('');

  // Check if user is superadmin
  useEffect(() => {
    if (user && user.role !== 'superadmin') {
      setError('Access denied. Superadmin privileges required.');
      setLoading(false);
      return;
    }
    fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/superadmin/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`/api/superadmin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!response.ok) {
        throw new Error('Failed to update user role');
      }

      // Update local state
      setUsers(users.map(user => 
        user._id === userId ? { ...user, role: newRole } : user
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const updateUserStatus = async (userId, newStatus) => {
    try {
      const response = await fetch(`/api/superadmin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      // Update local state
      setUsers(users.map(user => 
        user._id === userId ? { ...user, status: newStatus } : user
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleBatchAction = async () => {
    if (!batchAction || selectedUsers.length === 0) return;

    try {
      const updates = {};
      if (batchAction === 'role') {
        updates.role = 'user'; // Default role for batch update
      } else if (batchAction === 'status') {
        updates.status = 'active'; // Default status for batch update
      }

      const response = await fetch('/api/superadmin/users/batch', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userIds: selectedUsers, updates })
      });

      if (!response.ok) {
        throw new Error('Failed to update users');
      }

      // Refresh users list
      fetchUsers();
      setSelectedUsers([]);
      setBatchAction('');
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="superadmin-loading">Loading...</div>;
  }

  if (error) {
    return <div className="superadmin-error">{error}</div>;
  }

  if (!user || user.role !== 'superadmin') {
    return <div className="superadmin-error">Access denied. Superadmin privileges required.</div>;
  }

  return (
    <div className="superadmin-container">
      <h1>Superadmin Dashboard</h1>
      
      {/* Search and Batch Actions */}
      <div className="superadmin-controls">
        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        {selectedUsers.length > 0 && (
          <div className="batch-actions">
            <select 
              value={batchAction} 
              onChange={(e) => setBatchAction(e.target.value)}
              className="batch-select"
            >
              <option value="">Select action...</option>
              <option value="role">Change Role</option>
              <option value="status">Change Status</option>
            </select>
            <button onClick={handleBatchAction} className="batch-button">
              Apply to {selectedUsers.length} users
            </button>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedUsers.length === filteredUsers.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUsers(filteredUsers.map(user => user._id));
                    } else {
                      setSelectedUsers([]);
                    }
                  }}
                />
              </th>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user._id} className={user.status === 'banned' ? 'banned-user' : ''}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedUsers.includes(user._id)}
                    onChange={() => toggleUserSelection(user._id)}
                  />
                </td>
                <td>{user.username}</td>
                <td>{user.email}</td>
                <td>
                  <select
                    value={user.role || 'user'}
                    onChange={(e) => updateUserRole(user._id, e.target.value)}
                    className="role-select"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </select>
                </td>
                <td>
                  <select
                    value={user.status || 'active'}
                    onChange={(e) => updateUserStatus(user._id, e.target.value)}
                    className="status-select"
                  >
                    <option value="active">Active</option>
                    <option value="banned">Banned</option>
                  </select>
                </td>
                <td>
                  {user.lastLogin 
                    ? new Date(user.lastLogin).toLocaleDateString()
                    : 'Never'
                  }
                </td>
                <td>
                  <button 
                    onClick={() => updateUserRole(user._id, 'user')}
                    className="action-button"
                  >
                    Reset Role
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="superadmin-stats">
        <p>Total Users: {users.length}</p>
        <p>Active Users: {users.filter(u => u.status === 'active').length}</p>
        <p>Banned Users: {users.filter(u => u.status === 'banned').length}</p>
      </div>
    </div>
  );
};

export default SuperAdminPage; 